import { getConsentStatus } from "./consent";
import { whenMarketingMayLoad } from "./marketing-load";

// ChatGPT Ads (OpenAI) Measurement Pixel.
// Docs: https://developers.openai.com/ads/measurement-pixel
//
// Browser-only for now (no Conversions API key yet). Purchase and quote-lead
// events carry the same event IDs the other ad platforms use
// (purchase_<PaymentIntent id>, lead_<leadId>), so a server-side copy can be
// deduplicated later without changing this file.
//
// Runs on the live site only; on the dev preview add ?oaiq_debug=1 to the URL
// to enable it with SDK logging in the browser console.

const PIXEL_ID = "BGrouqMfTdPyFGrSSqzh2u";
const SDK_URL = "https://bzrcdn.openai.com/sdk/oaiq.min.js";

export type OaiqEvent =
  | "page_viewed"
  | "contents_viewed"
  | "items_added"
  | "checkout_started"
  | "order_created"
  | "lead_created"
  | "registration_completed";

export interface OaiqContent {
  id?: string;
  name?: string;
  content_type?: string;
  quantity?: number;
  /** Dollars; converted to cents. */
  price?: number;
}

export interface OaiqTrackOptions {
  /** Dollars; converted to cents. */
  value?: number;
  currency?: string;
  contents?: OaiqContent[];
  /** Dedup key shared with a future server-side event. */
  eventId?: string;
}

declare global {
  interface Window {
    oaiq?: ((...args: unknown[]) => void) & { q?: unknown[][] };
  }
}

const DATA_TYPE: Record<OaiqEvent, "contents" | "customer_action"> = {
  page_viewed: "contents",
  contents_viewed: "contents",
  items_added: "contents",
  checkout_started: "contents",
  order_created: "contents",
  lead_created: "customer_action",
  registration_completed: "customer_action",
};

function debugMode(): boolean {
  try {
    if (new URLSearchParams(window.location.search).has("oaiq_debug")) {
      sessionStorage.setItem("oaiq_debug", "1");
    }
    return sessionStorage.getItem("oaiq_debug") === "1";
  } catch {
    return false;
  }
}

function isEnabled(): boolean {
  if (typeof window === "undefined") return false;
  if (!import.meta.env.PROD && !debugMode()) return false;
  try {
    return getConsentStatus() !== "opted-out";
  } catch {
    return true;
  }
}

const cents = (dollars: number) => Math.round(dollars * 100);

let initialized = false;

/**
 * Installs the oaiq queue stub (so early events are kept), loads the SDK on
 * the shared marketing-load trigger and records the landing page view. No-op off the live site or after opt-out.
 */
export function initOpenAIPixel(): void {
  if (initialized || !isEnabled()) return;
  initialized = true;
  try {
    const w = window;
    if (!w.oaiq) {
      const q = function (...args: unknown[]) {
        q.q!.push(args);
      } as NonNullable<Window["oaiq"]>;
      q.q = [];
      w.oaiq = q;
      whenMarketingMayLoad(() => {
        // Visitor may have opted out between init and the trigger.
        if (!isEnabled()) return;
        const js = document.createElement("script");
        js.async = true;
        js.src = SDK_URL;
        document.head.appendChild(js);
      });
    }
    w.oaiq("init", { pixelId: PIXEL_ID, ...(debugMode() ? { debug: true } : {}) });
    oaiqTrack("page_viewed", { contents: [{ id: window.location.pathname, name: document.title, content_type: "page" }] });
  } catch {
    // Analytics must never break the app.
  }
}

export function oaiqTrack(event: OaiqEvent, opts: OaiqTrackOptions = {}): void {
  if (!isEnabled()) return;
  if (!initialized) {
    initOpenAIPixel();
    if (event === "page_viewed") return; // init already sent it
  }
  try {
    const currency = opts.currency ?? "USD";
    const data: Record<string, unknown> = { type: DATA_TYPE[event] };
    if (opts.value !== undefined && Number.isFinite(opts.value)) {
      data.amount = cents(opts.value);
      data.currency = currency;
    }
    if (DATA_TYPE[event] === "contents" && opts.contents?.length) {
      data.contents = opts.contents.map((c) => ({
        ...(c.id ? { id: c.id } : {}),
        ...(c.name ? { name: c.name } : {}),
        content_type: c.content_type ?? "product",
        ...(c.quantity !== undefined ? { quantity: Math.max(0, Math.round(c.quantity)) } : {}),
        ...(c.price !== undefined && Number.isFinite(c.price) ? { amount: cents(c.price), currency } : {}),
      }));
    }
    if (opts.eventId) window.oaiq?.("measure", event, data, { event_id: opts.eventId });
    else window.oaiq?.("measure", event, data);
  } catch {
    // Analytics must never break the app.
  }
}
